import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertCircle, CheckCircle2, Clock3, Coins, DatabaseZap, DollarSign, Download, RefreshCw, Wrench } from "lucide-react";
import { Button } from "@ncoder/ui";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { cn } from "@/lib/utils";
import { useObservabilityEvents } from "@/hooks/observability";
import { graphqlRequest } from "@/services/deployment/client";
import { buildObservabilityData, productOfService, PRODUCT_LABELS } from "@/services/observability";
import type {
  Period,
  ProductKey,
  InsightDetail,
  AgentSummary,
  AgentRoute,
  AgentRun,
  AgentUser,
  AgentTimePoint,
} from "@/services/observability";

const PRODUCTS: ProductKey[] = ["all", "agents", "migrations", "multi-agent", "testing", "salesforce-releases"];

// Resolve org members (userId -> email) so the per-user breakdown shows names,
// not raw UUIDs. Best-effort: falls back to {} (UI shows the id) if not allowed.
function useOrgUserMap(orgId: string | null) {
  return useQuery<Record<string, string>>({
    queryKey: ["observability-user-map", orgId],
    queryFn: async () => {
      try {
        const res = await graphqlRequest<{ orgAdminUsers?: Array<{ userId: string | null; user?: { id: string; email: string } | null }> }>(
          `query ObservabilityUserMap($organizationId: ID!) { orgAdminUsers(organizationId: $organizationId) { userId user { id email } } }`,
          { organizationId: orgId },
        );
        const map: Record<string, string> = {};
        for (const member of res.orgAdminUsers ?? []) {
          const id = member.userId || member.user?.id;
          if (id && member.user?.email) map[id] = member.user.email;
        }
        return map;
      } catch {
        return {};
      }
    },
    enabled: Boolean(orgId),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

function userLabel(userId: string | null | undefined, userMap: Record<string, string>): string {
  if (!userId) return "unknown";
  return userMap[userId] || `${userId.slice(0, 8)}…`;
}

// ─── Display-only constants ──────────────────────────────────────────────────

const periods: Period[] = ["24h", "7d", "30d", "90d"];

const chartPalettes = {
  agents: ["#2563eb", "#0891b2", "#16a34a", "#f59e0b", "#7c3aed", "#db2777"],
  tools: ["#0d9488", "#65a30d", "#ea580c", "#0284c7", "#9333ea", "#dc2626"],
  rag: ["#7c3aed", "#2563eb", "#059669", "#d97706", "#be123c", "#0f766e"],
};

// ─── Display utility functions ───────────────────────────────────────────────

function fmt(value: number | undefined, suffix = "") {
  if (value === undefined || Number.isNaN(value)) return "-";
  return `${value.toLocaleString()}${suffix}`;
}

function fmtTokens(value: number | undefined) {
  if (value === undefined || Number.isNaN(value)) return "-";
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return value.toLocaleString();
}

function fmtCost(value: number | undefined) {
  if (value === undefined || Number.isNaN(value)) return "-";
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: value < 1 ? 4 : 2, maximumFractionDigits: 4 })}`;
}

// CSV cell escaping with plain string ops (no regex).
function csvCell(value: unknown): string {
  const s = String(value ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.split('"').join('""')}"`;
  }
  return s;
}

function downloadCsv(filename: string, header: string[], rows: Array<Array<string | number>>): void {
  const lines = [header, ...rows].map((row) => row.map(csvCell).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function dateLabel(value: string | null) {
  if (!value) return "-";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function statusClass(ok: boolean) {
  return ok
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
    : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300";
}

// ─── Organism ────────────────────────────────────────────────────────────────

export function ObservabilityLayout() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const [period, setPeriod] = useState<Period>("7d");
  const [product, setProduct] = useState<ProductKey>("all");
  const [selectedInsight, setSelectedInsight] = useState<InsightDetail | null>(null);
  const [manualRefreshInFlight, setManualRefreshInFlight] = useState(false);

  const query = useObservabilityEvents(orgId, period);
  const userMap = useOrgUserMap(orgId).data ?? {};

  const data = query.data;
  // Segregate by product: re-aggregate the raw events for the selected surface.
  // "all" uses the full dataset. Tools/Services stay global (cross-product).
  const scoped = useMemo(() => {
    if (!data || product === "all" || !data.events) return data;
    const filtered = data.events.filter((event) => productOfService(event.service) === product);
    return { ...buildObservabilityData(filtered, orgId ?? "", period), events: filtered };
  }, [data, product, orgId, period]);

  const agents = scoped?.agents.available ? scoped.agents : null;
  const tools = data?.tools.available ? data.tools : null;
  const rag = data?.rag.available ? data.rag : null;

  const cards = useMemo(
    () => [
      {
        label: "Agent Turns",
        value: fmt(agents?.summary.requests),
        hint: `${fmt(agents?.summary.pass_rate, "%")} pass rate`,
        icon: Activity,
        color: chartPalettes.agents[0],
      },
      {
        label: "Total Cost",
        value: agents ? fmtCost(agents.summary.total_cost) : "-",
        hint: `${fmt(agents?.summary.requests)} runs in window`,
        icon: DollarSign,
        color: chartPalettes.agents[3],
      },
      {
        label: "Total Tokens",
        value: agents ? fmtTokens(agents.summary.total_tokens) : "-",
        hint: `${fmtTokens(agents?.summary.input_tokens)} in · ${fmtTokens(agents?.summary.output_tokens)} out`,
        icon: Coins,
        color: chartPalettes.agents[4],
      },
      {
        label: "P95 Latency",
        value: fmt(agents?.summary.p95_latency_s, "s"),
        hint: `Average iterations ${fmt(agents?.summary.avg_iterations)}`,
        icon: Clock3,
        color: chartPalettes.agents[1],
      },
      {
        label: "Tool Calls",
        value: fmt(tools?.summary.calls),
        hint: `${fmt(tools?.summary.success_rate, "%")} success rate`,
        icon: Wrench,
        color: chartPalettes.tools[0],
      },
      {
        label: "Service Runs",
        value: fmt(rag?.summary.runs),
        hint: `${fmt(rag?.summary.nodes_created)} events recorded`,
        icon: DatabaseZap,
        color: chartPalettes.rag[0],
      },
      {
        label: "Service Success",
        value: fmt(rag?.summary.success_rate, "%"),
        hint: `${fmt(rag?.summary.relationships_created)} tool calls`,
        icon: CheckCircle2,
        color: chartPalettes.rag[2],
      },
    ],
    [agents, period, rag, tools],
  );

  return (
    <div className="mx-auto flex max-w-[1600px] flex-col gap-5">
      {manualRefreshInFlight ? (
        <div className="fixed left-0 right-0 top-0 z-50 h-1 overflow-hidden bg-muted">
          <div className="h-full w-1/3 animate-[observability-refresh_1s_ease-in-out_infinite] bg-primary" />
          <style>{`
            @keyframes observability-refresh {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(400%); }
            }
          `}</style>
        </div>
      ) : null}

      {!orgId ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-200">
          Select an org before loading observability reports.
        </div>
      ) : null}

      <div className="flex flex-col gap-3 rounded-md border border-border bg-card p-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2">
          {periods.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setPeriod(item)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm font-medium",
                item === period
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-muted-foreground hover:text-foreground",
              )}
            >
              {item}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              // FULL per-run report (every line item), not the per-user summary.
              const runs = agents?.recent_runs ?? [];
              downloadCsv(
                `observability-${product}-runs-${period}.csv`,
                ["run_id", "created_at", "user", "user_id", "route", "intent", "iterations", "tool_calls", "input_tokens", "output_tokens", "total_tokens", "cost_usd", "latency_s", "final_pass", "has_error", "trace_id"],
                runs.map((r) => [
                  r.run_id, r.created_at ?? "", userLabel(r.user_id ?? "", userMap), r.user_id ?? "",
                  r.route, r.intent, r.iterations, r.tool_calls, r.input_tokens, r.output_tokens,
                  r.total_tokens, r.cost, r.latency_s, String(r.final_pass), String(r.has_error), r.trace_id ?? "",
                ]),
              );
            }}
            disabled={!agents || (agents.recent_runs?.length ?? 0) === 0}
            title="Download the full per-run report (all line items) as CSV"
          >
            <Download className="mr-2 h-4 w-4" />
            Download CSV
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!orgId || manualRefreshInFlight) return;
              setManualRefreshInFlight(true);
              try {
                await query.refetch();
              } finally {
                setManualRefreshInFlight(false);
              }
            }}
            disabled={!orgId || manualRefreshInFlight}
          >
            <RefreshCw className={cn("mr-2 h-4 w-4", manualRefreshInFlight && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {query.error ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
          Failed to load observability data from the backend observability contract.
        </div>
      ) : null}

      {data?.contractUnavailable ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-200">
          The selected backend does not expose the observability contract yet. Local UI is ready, but this cloud backend
          needs the observability schema and migrations deployed before live events can appear.
        </div>
      ) : null}

      <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-3">
        <div className="text-xs font-semibold uppercase text-muted-foreground">Product</div>
        <div className="flex flex-wrap gap-2">
          {PRODUCTS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setProduct(item);
                setSelectedInsight(null);
              }}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm font-medium",
                item === product
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-muted-foreground hover:text-foreground",
              )}
            >
              {PRODUCT_LABELS[item]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-7">
        {cards.map((card) => (
          <SummaryCard key={card.label} {...card} />
        ))}
      </div>

      <div className="rounded-md border border-border bg-card p-4">
        {selectedInsight ? <InsightPanel insight={selectedInsight} onClear={() => setSelectedInsight(null)} /> : null}
        {data?.agents.available === false ? (
          <SectionUnavailable name="Agent" error={data.agents.error} />
        ) : (
          <AgentsReport
            summary={agents?.summary}
            routes={agents?.routes ?? []}
            users={agents?.users ?? []}
            userMap={userMap}
            timeSeries={agents?.time_series ?? []}
            runs={(agents?.recent_runs ?? []).slice(0, 40)}
            selectedInsightKey={selectedInsight?.key ?? null}
            onInsight={setSelectedInsight}
          />
        )}
      </div>
    </div>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  hint,
  icon: Icon,
  color = "#2563eb",
}: {
  label: string;
  value: string;
  hint: string;
  icon: typeof Activity;
  color?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-4" style={{ borderTopColor: color, borderTopWidth: 3 }}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase text-muted-foreground">{label}</div>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="mt-3 text-2xl font-semibold tracking-tight text-foreground">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

function DonutGauge({ label, value, color = "#2563eb" }: { label: string; value: number | undefined; color?: string }) {
  const safeValue = Math.max(0, Math.min(100, value ?? 0));
  const circumference = 2 * Math.PI * 35;
  const offset = circumference - (safeValue / 100) * circumference;
  return (
    <div className="flex items-center gap-3">
      <svg viewBox="0 0 88 88" className="h-20 w-20 shrink-0">
        <circle cx="44" cy="44" r="35" fill="none" stroke="currentColor" strokeWidth="9" className="text-muted" />
        <circle
          cx="44"
          cy="44"
          r="35"
          fill="none"
          stroke="currentColor"
          strokeWidth="9"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ color }}
          transform="rotate(-90 44 44)"
        />
        <text x="44" y="49" textAnchor="middle" className="fill-foreground text-[16px] font-semibold">
          {Math.round(safeValue)}%
        </text>
      </svg>
      <div>
        <div className="text-xs font-semibold uppercase text-muted-foreground">{label}</div>
        <div className="mt-1 text-sm text-muted-foreground">Click report elements to inspect the underlying slice.</div>
      </div>
    </div>
  );
}

function SectionUnavailable({ name, error }: { name: string; error?: string }) {
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-200">
      <div className="flex items-center gap-2 font-semibold">
        <AlertCircle className="h-4 w-4" />
        {name} data is not available from the metrics database.
      </div>
      {error ? <div className="mt-2 font-mono text-xs opacity-80">{error}</div> : null}
    </div>
  );
}

function EmptyRows({ label }: { label: string }) {
  return (
    <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function MiniBarChart({
  rows,
  valueKey,
  labelKey,
  suffix = "",
  selectedKey,
  onSelect,
  palette = chartPalettes.agents,
}: {
  rows: Array<Record<string, string | number | null>>;
  valueKey: string;
  labelKey: string;
  suffix?: string;
  selectedKey?: string | null;
  onSelect?: (row: Record<string, string | number | null>) => void;
  palette?: string[];
}) {
  const max = Math.max(...rows.map((row) => Number(row[valueKey] ?? 0)), 1);
  if (rows.length === 0) return <EmptyRows label="No data for this report." />;
  return (
    <div className="space-y-3">
      {rows.map((row, index) => {
        const value = Number(row[valueKey] ?? 0);
        const label = String(row[labelKey] ?? "");
        const selected = selectedKey === label;
        const color = palette[index % palette.length];
        return (
          <button
            key={`${label}-${value}`}
            type="button"
            onClick={() => onSelect?.(row)}
            className={cn(
              "grid w-full grid-cols-[140px_1fr_72px] items-center gap-3 rounded-md border border-transparent px-2 py-1.5 text-left text-sm transition-colors",
              onSelect && "hover:border-border hover:bg-muted/40",
              selected && "border-primary bg-primary/10",
            )}
          >
            <div className="truncate font-medium" title={label}>
              {label}
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(4, (value / max) * 100)}%`,
                  background: `linear-gradient(90deg, ${color}99, ${color})`,
                }}
              />
            </div>
            <div className="text-right tabular-nums text-muted-foreground">{fmt(value, suffix)}</div>
          </button>
        );
      })}
    </div>
  );
}

function Sparkline({
  points,
  valueKey,
  selectedKey,
  onSelect,
  palette = chartPalettes.agents,
}: {
  points: Array<Record<string, string | number | null>>;
  valueKey: string;
  selectedKey?: string | null;
  onSelect?: (point: Record<string, string | number | null>) => void;
  palette?: string[];
}) {
  const values = points.map((point) => Number(point[valueKey] ?? 0));
  const max = Math.max(...values, 1);
  if (values.length === 0) return <div className="h-28 rounded-md bg-muted/30" />;
  return (
    <div className="flex h-28 items-end gap-1 rounded-md border border-border bg-muted/20 p-3">
      {values.map((value, index) => (
        <button
          key={`${value}-${index}`}
          type="button"
          onClick={() => onSelect?.(points[index])}
          className={cn("min-w-2 flex-1 rounded-t transition-opacity hover:opacity-80")}
          style={{
            height: `${Math.max(6, (value / max) * 100)}%`,
            backgroundColor:
              selectedKey === String(points[index]?.bucket ?? index)
                ? "currentColor"
                : palette[index % palette.length],
          }}
          title={`${value}`}
        />
      ))}
    </div>
  );
}

function ReportPanel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-md border border-border bg-background p-4">
      <div className="mb-4">
        <h2 className="text-sm font-semibold uppercase text-muted-foreground">{title}</h2>
        {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function InsightPanel({ insight, onClear }: { insight: InsightDetail; onClear: () => void }) {
  return (
    <div className="mb-4 rounded-md border border-primary/30 bg-primary/5 p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase text-primary">Selected insight</div>
          <div className="mt-1 text-base font-semibold text-foreground">{insight.title}</div>
          <p className="mt-1 text-sm text-muted-foreground">{insight.subtitle}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onClear}>
          Clear
        </Button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {insight.metrics.map((metric) => (
          <div key={metric.label} className="rounded-md border border-border bg-background p-3">
            <div className="text-xs font-semibold uppercase text-muted-foreground">{metric.label}</div>
            <div className="mt-1 text-lg font-semibold">{metric.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Report subcomponents ─────────────────────────────────────────────────────

function AgentsReport({
  summary,
  routes,
  users,
  userMap,
  timeSeries,
  runs,
  selectedInsightKey,
  onInsight,
}: {
  summary?: AgentSummary;
  routes: AgentRoute[];
  users: AgentUser[];
  userMap: Record<string, string>;
  timeSeries: AgentTimePoint[];
  runs: AgentRun[];
  selectedInsightKey: string | null;
  onInsight: (insight: InsightDetail) => void;
}) {
  const routeRows = routes.map((route) => ({ label: route.route, value: route.turns }));
  const selectedRoute = selectedInsightKey?.startsWith("agent-route:")
    ? selectedInsightKey.replace("agent-route:", "")
    : null;
  const selectedDay = selectedInsightKey?.startsWith("agent-day:")
    ? selectedInsightKey.replace("agent-day:", "")
    : null;
  const filteredRuns = selectedRoute ? runs.filter((run) => run.route === selectedRoute) : runs;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-[1fr_420px]">
        <ReportPanel title="Agent Volume Trend" description="Daily agent turns for the selected org and time window.">
          <Sparkline
            points={timeSeries}
            valueKey="turns"
            selectedKey={selectedDay}
            palette={chartPalettes.agents}
            onSelect={(point) => {
              const bucket = String(point.bucket ?? "");
              onInsight({
                key: `agent-day:${bucket}`,
                title: `Agent traffic on ${dateLabel(bucket)}`,
                subtitle: "Daily agent workload, pass rate, latency, and spend.",
                metrics: [
                  { label: "Turns", value: fmt(Number(point.turns)) },
                  { label: "Pass Rate", value: fmt(Number(point.pass_rate), "%") },
                  { label: "Avg Latency", value: fmt(Number(point.avg_latency_s), "s") },
                  {
                    label: "Cost",
                    value: `$${Number(point.cost ?? 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}`,
                  },
                ],
              });
            }}
          />
          <div className="mt-4">
            <DonutGauge label="Agent Pass Rate" value={summary?.pass_rate} color={chartPalettes.agents[2]} />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-muted-foreground">Requests</div>
              <div className="font-semibold">{fmt(summary?.requests)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Pass Rate</div>
              <div className="font-semibold">{fmt(summary?.pass_rate, "%")}</div>
            </div>
            <div>
              <div className="text-muted-foreground">P95 Latency</div>
              <div className="font-semibold">{fmt(summary?.p95_latency_s, "s")}</div>
            </div>
          </div>
        </ReportPanel>
        <ReportPanel title="Route Distribution" description="Which agent routes are carrying the workload.">
          <MiniBarChart
            rows={routeRows}
            labelKey="label"
            valueKey="value"
            selectedKey={selectedRoute}
            palette={chartPalettes.agents}
            onSelect={(row) => {
              const route = routes.find((item) => item.route === row.label);
              if (!route) return;
              onInsight({
                key: `agent-route:${route.route}`,
                title: `${route.route} route`,
                subtitle: "Route-level throughput, reliability, latency, and average spend.",
                metrics: [
                  { label: "Turns", value: fmt(route.turns) },
                  { label: "Pass Rate", value: fmt(route.pass_rate, "%") },
                  { label: "Avg Latency", value: fmt(route.avg_latency_s, "s") },
                  { label: "Avg Cost", value: `$${route.avg_cost}` },
                ],
              });
            }}
          />
        </ReportPanel>
      </div>

      <ReportPanel title="Route Quality" description="Pass rate, latency, and cost by route.">
        <div className="overflow-x-auto">
          {routes.length === 0 ? (
            <EmptyRows label="No route data for this window." />
          ) : (
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Route</th>
                  <th className="px-3 py-2 text-right">Turns</th>
                  <th className="px-3 py-2 text-right">Pass</th>
                  <th className="px-3 py-2 text-right">Avg Latency</th>
                  <th className="px-3 py-2 text-right">Tokens</th>
                  <th className="px-3 py-2 text-right">Avg Cost</th>
                  <th className="px-3 py-2 text-right">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((route) => (
                  <tr
                    key={route.route}
                    className={cn(
                      "cursor-pointer border-t border-border hover:bg-muted/40",
                      selectedRoute === route.route && "bg-primary/10",
                    )}
                    onClick={() =>
                      onInsight({
                        key: `agent-route:${route.route}`,
                        title: `${route.route} route`,
                        subtitle: "Route-level throughput, reliability, latency, tokens, and spend.",
                        metrics: [
                          { label: "Turns", value: fmt(route.turns) },
                          { label: "Pass Rate", value: fmt(route.pass_rate, "%") },
                          { label: "Avg Latency", value: fmt(route.avg_latency_s, "s") },
                          { label: "Total Tokens", value: fmtTokens(route.total_tokens) },
                          { label: "Avg Cost", value: fmtCost(route.avg_cost) },
                          { label: "Total Cost", value: fmtCost(route.total_cost) },
                        ],
                      })
                    }
                  >
                    <td className="max-w-[200px] truncate px-3 py-2 font-medium">{route.route}</td>
                    <td className="px-3 py-2 text-right">{route.turns}</td>
                    <td className="px-3 py-2 text-right">{route.pass_rate}%</td>
                    <td className="px-3 py-2 text-right">{route.avg_latency_s}s</td>
                    <td className="px-3 py-2 text-right" title={`${route.input_tokens.toLocaleString()} in / ${route.output_tokens.toLocaleString()} out`}>
                      {fmtTokens(route.total_tokens)}
                    </td>
                    <td className="px-3 py-2 text-right">{fmtCost(route.avg_cost)}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmtCost(route.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </ReportPanel>

      <ReportPanel title="Consumption by User" description="Cost and token spend per user across all routes — use Download CSV to export.">
        <div className="overflow-x-auto">
          {users.length === 0 ? (
            <EmptyRows label="No per-user usage for this window." />
          ) : (
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-right">Runs</th>
                  <th className="px-3 py-2 text-right">Input Tokens</th>
                  <th className="px-3 py-2 text-right">Output Tokens</th>
                  <th className="px-3 py-2 text-right">Total Tokens</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                  <th className="px-3 py-2 text-right">Pass</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.user_id} className="border-t border-border hover:bg-muted/40">
                    <td className="px-3 py-2 text-xs" title={user.user_id}>{userLabel(user.user_id, userMap)}</td>
                    <td className="px-3 py-2 text-right">{user.runs}</td>
                    <td className="px-3 py-2 text-right">{user.input_tokens.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{user.output_tokens.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-medium">{user.total_tokens.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmtCost(user.cost)}</td>
                    <td className="px-3 py-2 text-right">{user.pass_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </ReportPanel>

      <ReportPanel title="Recent Agent Runs" description="Drill-down rows backing the report.">
        <div className="overflow-x-auto">
          {filteredRuns.length === 0 ? (
            <EmptyRows label="No agent runs for this slice." />
          ) : (
            <table className="w-full min-w-[1040px] text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Run</th>
                  <th className="px-3 py-2 text-left">Route</th>
                  <th className="px-3 py-2 text-left">Question / Name</th>
                  <th className="px-3 py-2 text-right">Tokens</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                  <th className="px-3 py-2 text-right">Latency</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredRuns.map((run) => (
                  <tr key={run.run_id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs">{run.run_id.slice(0, 10)}</td>
                    <td className="px-3 py-2">{run.route}</td>
                    <td className="max-w-[360px] truncate px-3 py-2" title={run.intent}>{run.intent}</td>
                    <td
                      className="whitespace-nowrap px-3 py-2 text-right"
                      title={`${run.input_tokens.toLocaleString()} in / ${run.output_tokens.toLocaleString()} out`}
                    >
                      {fmtTokens(run.total_tokens)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">{fmtCost(run.cost)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">{run.latency_s}s</td>
                    <td className="px-3 py-2">
                      <span className={cn("rounded border px-2 py-0.5 text-xs", statusClass(run.final_pass && !run.has_error))}>
                        {run.final_pass && !run.has_error ? "passed" : "failed"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{dateLabel(run.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </ReportPanel>
    </div>
  );
}
